/**
 * /settings/shopify — Shopify revenue integration.
 *
 * Two operations:
 *   POST /api/saas/shopify/test  — verify credentials (no DB write)
 *   POST /api/saas/shopify/sync  — pull paid orders → AR invoices
 *
 * Credentials are not stored server-side — entered per-session (same
 * model as Stripe / Chargebee chat import). Sync is idempotent.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SyncIcon from "@mui/icons-material/Sync";
import WebhookIcon from "@mui/icons-material/Webhook";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { api, errorMessage } from "@/lib/api";

export const Route = createFileRoute("/_auth/settings/shopify")({
  component: ShopifySettingsPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

type TestResult = {
  ok: boolean;
  latency_ms: number;
  detail: string;
  shop_name: string | null;
  order_count_sample: number | null;
  error: string | null;
};

type ConnectionOut = {
  id: string;
  shop_domain: string;
  shopify_webhook_id: number | null;
  is_active: boolean;
  registered_at: string;
  last_event_at: string | null;
};

type SyncResult = {
  success: boolean;
  orders_imported: number;
  orders_skipped: number;
  customers_created: number;
  customers_found: number;
  total_revenue_dollars: number;
  sample_orders: string[];
  errors: string[];
  message: string;
};

// ── SecretField ───────────────────────────────────────────────────────────────

function SecretField({
  label,
  value,
  onChange,
  placeholder,
  monospace,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  monospace?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <TextField
      label={label}
      type={show ? "text" : "password"}
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      onPaste={(e) => {
        const pasted = e.clipboardData.getData("text");
        const cleaned = pasted.trim();
        if (pasted !== cleaned) {
          e.preventDefault();
          onChange(cleaned);
        }
      }}
      inputProps={monospace ? { style: { fontFamily: "monospace", fontSize: 13 } } : {}}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShow((s) => !s)} edge="end">
              {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function ago(iso: string | null | undefined): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function ShopifySettingsPage() {
  const qc = useQueryClient();
  const [shopDomain, setShopDomain] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [sinceDate, setSinceDate] = useState("");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  const canSubmit = shopDomain.trim().length > 0 && accessToken.trim().length > 4;

  // ── Webhook connections ──────────────────────────────────────────────────
  const connectionsQuery = useQuery({
    queryKey: ["shopify-connections"],
    queryFn: () => api<ConnectionOut[]>("/api/saas/shopify/connections"),
  });

  const registerMutation = useMutation({
    mutationFn: () =>
      api<ConnectionOut>("/api/saas/shopify/connections", {
        method: "POST",
        body: JSON.stringify({ shop_domain: shopDomain.trim(), access_token: accessToken.trim() }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopify-connections"] }),
  });

  const disconnectMutation = useMutation({
    mutationFn: (id: string) =>
      api(`/api/saas/shopify/connections/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopify-connections"] }),
  });

  const connections = connectionsQuery.data ?? [];

  const testMutation = useMutation({
    mutationFn: () =>
      api<TestResult>("/api/saas/shopify/test", {
        method: "POST",
        body: JSON.stringify({ shop_domain: shopDomain.trim(), access_token: accessToken.trim() }),
      }),
    onSuccess: (r) => {
      setTestResult(r);
      setSyncResult(null);
    },
    onError: (e) => {
      setTestResult({ ok: false, latency_ms: 0, detail: errorMessage(e), shop_name: null, order_count_sample: null, error: errorMessage(e) });
    },
  });

  const syncMutation = useMutation({
    mutationFn: () =>
      api<SyncResult>("/api/saas/shopify/sync", {
        method: "POST",
        body: JSON.stringify({
          shop_domain: shopDomain.trim(),
          access_token: accessToken.trim(),
          since_date: sinceDate.trim() || null,
        }),
      }),
    onSuccess: (r) => {
      setSyncResult(r);
    },
    onError: (e) => {
      setSyncResult({ success: false, orders_imported: 0, orders_skipped: 0, customers_created: 0, customers_found: 0, total_revenue_dollars: 0, sample_orders: [], errors: [errorMessage(e)], message: errorMessage(e) });
    },
  });

  const isPending = testMutation.isPending || syncMutation.isPending;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              Shopify Revenue Integration
            </h1>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Import paid orders → AR invoices · customers · revenue accounts
            </span>
          </div>
          <Button
            href="https://help.ledgercore.io/saas/shopify/sync-orders"
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: "0.85rem !important" }} />}
          >
            Docs
          </Button>
        </Stack>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4 space-y-4 max-w-2xl">

        {/* Credentials */}
        <Card>
          <CardHeader
            title="Shopify credentials"
            subheader="Enter your Shopify shop domain and Admin API access token. These are not stored — re-enter each sync."
          />
          <CardContent>
            <Stack spacing={2}>
              <TextField
                label="Shop domain"
                size="small"
                fullWidth
                value={shopDomain}
                onChange={(e) => setShopDomain(e.target.value)}
                placeholder="acme  (or acme.myshopify.com)"
                helperText="The subdomain of your Shopify store — everything before .myshopify.com"
              />

              <SecretField
                label="Admin API access token"
                value={accessToken}
                onChange={setAccessToken}
                placeholder="shpat_…"
                monospace
              />

              {/* How to get it */}
              <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
                <Stack spacing={0.5}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                      How to get an access token
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      endIcon={<OpenInNewIcon sx={{ fontSize: "0.85rem !important" }} />}
                      href="https://admin.shopify.com/settings/apps/development"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: "0.75rem", py: 0, minWidth: 0 }}
                    >
                      Shopify Admin
                    </Button>
                  </Stack>
                  <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                    {[
                      "Go to Shopify Admin → Settings → Apps and sales channels",
                      "Click Develop apps (enable developer tools if prompted)",
                      "Click Create an app — name it \"Ledger Core Sync\"",
                      "Go to Configuration → Admin API integration",
                      "Enable read_orders scope, then Save",
                      "Click Install app, then copy the Admin API access token",
                    ].map((step, i) => (
                      <Typography key={i} component="li" variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
                        {step}
                      </Typography>
                    ))}
                  </Box>
                  <Typography variant="caption" color="warning.main">
                    ⚠ The token is shown once in Shopify. Copy and paste it here immediately.
                  </Typography>
                </Stack>
              </Box>

              <Button
                variant="outlined"
                size="small"
                disabled={!canSubmit || isPending}
                onClick={() => testMutation.mutate()}
                startIcon={testMutation.isPending ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                sx={{ alignSelf: "flex-start" }}
              >
                {testMutation.isPending ? "Testing…" : "Test connection"}
              </Button>

              {testResult && (
                <Alert severity={testResult.ok ? "success" : "error"} sx={{ fontSize: "0.8rem" }}>
                  {testResult.detail}
                  {testResult.ok && testResult.order_count_sample != null && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {testResult.order_count_sample.toLocaleString()} paid orders ready to import
                    </Typography>
                  )}
                  {testResult.error && !testResult.ok && (
                    <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5, fontFamily: "monospace" }}>
                      {testResult.error}
                    </Typography>
                  )}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Sync options */}
        <Card>
          <CardHeader
            title="Import orders"
            subheader="Pulls all paid Shopify orders and creates AR invoices in Ledger Core. Safe to run multiple times — existing orders are skipped."
            avatar={<ReceiptLongIcon color="action" />}
          />
          <CardContent>
            <Stack spacing={2}>
              <TextField
                label="Import orders since (optional)"
                size="small"
                type="date"
                value={sinceDate}
                onChange={(e) => setSinceDate(e.target.value)}
                helperText="Leave blank to import all paid orders. Set a date to import only recent orders."
                InputLabelProps={{ shrink: true }}
                sx={{ maxWidth: 260 }}
              />

              <Divider />

              <Stack direction="row" alignItems="center" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={syncMutation.isPending ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
                  disabled={!canSubmit || isPending}
                  onClick={() => syncMutation.mutate()}
                >
                  {syncMutation.isPending ? "Importing…" : "Import orders"}
                </Button>
                {syncResult?.success && (
                  <Button
                    component={Link}
                    to="/ar/invoices"
                    variant="outlined"
                    size="small"
                  >
                    View AR Invoices →
                  </Button>
                )}
              </Stack>

              {syncResult && (
                <Alert severity={syncResult.success ? "success" : "error"}>
                  <Typography variant="body2" fontWeight={500}>
                    {syncResult.message}
                  </Typography>
                  {syncResult.success && (
                    <Box sx={{ mt: 1 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`${syncResult.orders_imported} orders imported`} color="success" variant="outlined" />
                        {syncResult.orders_skipped > 0 && (
                          <Chip size="small" label={`${syncResult.orders_skipped} skipped`} variant="outlined" />
                        )}
                        <Chip size="small" label={`${syncResult.customers_created} customers created`} color="info" variant="outlined" />
                        {syncResult.customers_found > 0 && (
                          <Chip size="small" label={`${syncResult.customers_found} customers matched`} variant="outlined" />
                        )}
                        <Chip size="small" label={`$${syncResult.total_revenue_dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} revenue`} color="primary" variant="outlined" />
                      </Stack>
                      {syncResult.sample_orders.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Sample: {syncResult.sample_orders.join(" · ")}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {syncResult.errors.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {syncResult.errors.map((e, i) => (
                        <Typography key={i} variant="caption" color="error" sx={{ display: "block", fontFamily: "monospace", fontSize: 11 }}>
                          {e}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Webhook — real-time sync */}
        <Card>
          <CardHeader
            title="Real-time webhook"
            subheader="Register an orders/paid webhook so new Shopify orders appear in Ledger Core automatically — no manual syncs needed."
            avatar={<WebhookIcon color="action" />}
          />
          <CardContent>
            <Stack spacing={2}>
              {connectionsQuery.isLoading ? (
                <CircularProgress size={20} />
              ) : connections.length > 0 ? (
                <Stack spacing={1}>
                  {connections.map((c) => (
                    <Box
                      key={c.id}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        border: 1, borderColor: "success.light", borderRadius: 1.5,
                        px: 2, py: 1.25, bgcolor: "action.hover",
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                        <div>
                          <Typography variant="body2" fontWeight={500}>{c.shop_domain}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Registered · last event {ago(c.last_event_at)}
                            {c.shopify_webhook_id && ` · ID ${c.shopify_webhook_id}`}
                          </Typography>
                        </div>
                      </Stack>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={disconnectMutation.isPending}
                        onClick={() => disconnectMutation.mutate(c.id)}
                        title="Disconnect webhook"
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Typography variant="caption" color="text.secondary">
                    New paid orders will automatically create AR invoices. Disconnect to stop.
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
                    No webhook registered. Enter your shop domain and access token above, then click <strong>Register webhook</strong> to enable real-time sync.
                  </Alert>
                  <Button
                    variant="outlined"
                    startIcon={registerMutation.isPending ? <CircularProgress size={14} /> : <WebhookIcon />}
                    disabled={!canSubmit || registerMutation.isPending}
                    onClick={() => registerMutation.mutate()}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {registerMutation.isPending ? "Registering…" : "Register webhook"}
                  </Button>
                  {registerMutation.isError && (
                    <Alert severity="error" sx={{ fontSize: "0.8rem" }}>
                      {errorMessage(registerMutation.error)}
                    </Alert>
                  )}
                  {registerMutation.isSuccess && (
                    <Alert severity="success" sx={{ fontSize: "0.8rem" }}>
                      Webhook registered — new paid orders will sync automatically.
                    </Alert>
                  )}
                </Stack>
              )}

              <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.25 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>How it works:</strong> Shopify will POST to{" "}
                  <code style={{ fontSize: 11 }}>/api/saas/shopify/webhook</code>{" "}
                  whenever an order is paid. Ledger Core verifies the HMAC signature
                  and creates the AR invoice automatically. The access token is only
                  used during registration and is not stored.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* What gets imported */}
        <Card variant="outlined">
          <CardContent sx={{ pb: "12px !important" }}>
            <Typography variant="subtitle2" gutterBottom>What gets imported</Typography>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& td, & th": { py: 0.5, px: 1, fontSize: "0.78rem" } }}>
              <thead>
                <tr>
                  <Box component="th" sx={{ textAlign: "left", color: "text.secondary", borderBottom: 1, borderColor: "divider" }}>Shopify</Box>
                  <Box component="th" sx={{ textAlign: "left", color: "text.secondary", borderBottom: 1, borderColor: "divider" }}>Ledger Core</Box>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Customer name + email", "Party (customer) record"],
                  ["Order #1042", "AR Invoice SH-1042"],
                  ["Line items (title, qty, price)", "AR Invoice lines → revenue account (4xxx)"],
                  ["total_price", "Invoice total + received_amount"],
                  ["processed_at / created_at", "Invoice date + fiscal period"],
                  ["currency", "Invoice currency code"],
                  ["billing_address (guest checkouts)", "Party name fallback"],
                ].map(([from, to], i) => (
                  <tr key={i}>
                    <Box component="td" sx={{ color: "text.secondary", fontFamily: "monospace", fontSize: "0.75rem" }}>{from}</Box>
                    <td>{to}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Only <strong>paid</strong> orders are imported. Refunds, pending orders, and draft orders are skipped.
              Each order requires an open fiscal period covering its date — create periods in{" "}
              <Link to="/periods" className="underline text-indigo-600">Accounting → Periods</Link> first.
            </Typography>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
